/*
 * Copyright (c) 2020. World Wide Generation Ltd
 */

import React, { useMemo } from 'react';
import { useAppSelector } from '../../../reducers';
import { getQuestionId, getUtrFromSurvey } from '../../../selectors/survey';
import { getCurrentUser } from '../../../selectors/user';
import { useQuestionForm } from './QuestionReducer';
import { hasStakeholderAccess } from '../../../utils/universalTrackerValue';
import { getQuestionAssuranceStatus } from '../../../utils/assurance';
import { generateDisabledUtrs, hasUTRVFilter } from '@utils/survey/getDisableUtrs'
import { BlueprintContributions, ScopeQuestionGroup, SurveyAddAssurance } from '@g17eco/types/survey';
import { SurveyActionData } from '@g17eco/types/surveyAction';
import queryString from 'query-string';
import { NavigationButtons } from '@g17eco/molecules/question/NavigationButtons';
import { QuestionMenu } from './QuestionMenu';
import { useQuestionIds } from '../../../hooks/useQuestionIds';
import { useBookmarks } from '@features/survey-question-list/hooks/useBookmarks';
import { Document } from 'flexsearch';
import { useLocation } from 'react-router-dom';
import { QuestionContainerHeaderBreadcrumbs } from './QuestionContainerHeaderBreadcrumbs';
import { isAggregatedSurvey } from '@utils/survey';
import { LoadingPlaceholder } from '@g17eco/molecules/loader-container';

interface QuestionContainerProps {
  surveyId: string;
  questionId: string;
  questionIndex: string;
  handleAddAssurance: (survey: SurveyAddAssurance) => void;
  toggleMapping: () => void;
  surveyGroups: ScopeQuestionGroup[];
  survey: SurveyActionData;
  blueprint: BlueprintContributions;
  searchIndex?: Document<unknown, false>;
  handleGoToQuestion: ({ id, index }: { id: string, index?: number }) => void;
  rootBreadcrumb: {
    label: string;
    url: string;
  },
  handleReload: () => Promise<void>;
}

export function QuestionContainerHeaderStateless(props: QuestionContainerProps) {
  const {
    surveyId,
    questionId,
    questionIndex,
    surveyGroups,
    survey,
    blueprint,
    searchIndex,
    handleAddAssurance,
    handleGoToQuestion,
    rootBreadcrumb,
    toggleMapping,
    handleReload,
  } = props;

  // @TODO - Remove these hooks
  const location = useLocation();

  const user = useAppSelector(getCurrentUser);

  const { bookmarks } = useBookmarks(surveyId);
  const { utr, utrv } = getUtrFromSurvey({ survey, surveyGroups, questionId });

  const disableUtrs = useMemo(() => {
    const query = queryString.parse(location.search, { arrayFormat: 'bracket' });
    if (!hasUTRVFilter(query)) {
      return [];
    }

    return generateDisabledUtrs({
      surveyGroups,
      blueprint,
      searchIndex,
      user,
      ...query,
      filterByBookmarks: query.filterByBookmarks === 'true',
      bookmarks,
    });
  }, [blueprint, bookmarks, location.search, searchIndex, surveyGroups, user]);

  const {
    questionListIds,
    currentQuestionIndex,
    altCode: alternativeCode,
  } = useQuestionIds(surveyGroups, questionIndex, questionId, disableUtrs)

  const { state } = useQuestionForm();
  const { assurancePortfolio, hasPortfolio, alternativeCodeOverride } = state;

  const isAssuranceStakeholder = React.useMemo(() => {
    const firstAssurance = assurancePortfolio?.assurances?.[0];
    return hasStakeholderAccess(firstAssurance?.stakeholders, user?._id)
  }, [assurancePortfolio, user]);

  const isLoaded = user && blueprint;
  const isUTRLoaded = utr && utrv;

  if (isLoaded && !isUTRLoaded) {
    return null;
  }

  const isAggregate = isAggregatedSurvey(survey.type);
  const isCompleted = Boolean(survey.completedDate);
  const selectedAltCode = alternativeCodeOverride || alternativeCode || utr?.getType() || '';

  const navigationDisabled = !isLoaded || questionListIds.length <= 1;
  const getAssuranceStatus = (utrvId: string) => getQuestionAssuranceStatus(assurancePortfolio, utrvId);
  const goNext = () => handleGoToQuestion(getQuestionId(questionListIds, questionId, currentQuestionIndex, true));
  const goPrevious = () => handleGoToQuestion(getQuestionId(questionListIds, questionId, currentQuestionIndex, false));

  return (
    <LoadingPlaceholder height={29} isLoading={!isLoaded} className='mb-0' >
      <div className='w-100 d-flex justify-content-between align-items-center flex-wrap flex-md-nowrap'>
        <QuestionContainerHeaderBreadcrumbs
          rootBreadcrumb={rootBreadcrumb}
          survey={survey}
          utr={utr}
          selectedAltCode={selectedAltCode}
        />
        <div className='ml-auto'>
          <NavigationButtons isDisabled={navigationDisabled} goPrevious={goPrevious} goNext={goNext} />
        </div>
        <div className='ml-2'>
          {isLoaded && utr && utrv && !isAggregate ?
            <QuestionMenu
              toggleMapping={toggleMapping}
              utr={utr}
              utrv={utrv}
              user={user}
              isCompleted={isCompleted}
              handleAddAssurance={handleAddAssurance}
              survey={survey}
              hasPortfolio={hasPortfolio}
              getAssuranceStatus={getAssuranceStatus}
              isAssuranceStakeholder={isAssuranceStakeholder}
              alternativeCode={selectedAltCode}
              handleReload={handleReload}
            /> : null
          }
        </div>
      </div>
    </LoadingPlaceholder>
  );
}
